import { ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import type { BooleanInput } from '@angular/cdk/coercion';
import {
	afterNextRender,
	booleanAttribute,
	computed,
	contentChild,
	contentChildren,
	Directive,
	effect,
	forwardRef,
	inject,
	Injector,
	input,
	linkedSignal,
	model,
	signal,
	untracked,
} from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BrnFieldControl, provideBrnLabelable } from '@spartan-ng/brain/field';
import { type ChangeFn, type TouchFn } from '@spartan-ng/brain/forms';
import { BrnPopover } from '@spartan-ng/brain/popover';
import { BrnComboboxContent } from './brn-combobox-content';
import type { BrnComboboxInput } from './brn-combobox-input';
import { type BrnComboboxItem } from './brn-combobox-item';
import { BrnComboboxItemToken } from './brn-combobox-item.token';
import type { BrnComboboxList } from './brn-combobox-list';
import {
	type BrnComboboxBase,
	type ComboboxFilter,
	type ComboboxFilterOptions,
	ComboboxInputMode,
	type ComboboxItemEqualToValue,
	type ComboboxItemToString,
	injectBrnComboboxConfig,
	provideBrnComboboxBase,
} from './brn-combobox.token';

export const BRN_COMBOBOX_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => BrnCombobox),
	multi: true,
};

@Directive({
	selector: '[brnCombobox]',
	providers: [BRN_COMBOBOX_VALUE_ACCESSOR, provideBrnComboboxBase(BrnCombobox), provideBrnLabelable(BrnCombobox)],
	hostDirectives: [BrnFieldControl],
	host: {
		'(focusout)': '_onFocusOut($event)',
	},
})
export class BrnCombobox<T> implements BrnComboboxBase<T>, ControlValueAccessor {
	private readonly _injector = inject(Injector);
	private readonly _fieldControl = inject(BrnFieldControl, { optional: true });

	private readonly _config = injectBrnComboboxConfig<T>();

	/** Access the popover if present */
	private readonly _brnPopover = inject(BrnPopover, { optional: true });

	public readonly controlState = this._fieldControl?.controlState;

	/** Whether the combobox is disabled */
	public readonly disabled = input<boolean, BooleanInput>(false, { transform: booleanAttribute });

	protected readonly _disabled = linkedSignal(this.disabled);

	/** @internal The disabled state as a readonly signal */
	public readonly disabledState = this._disabled.asReadonly();

	/** Options for filtering the combobox items */
	public readonly filterOptions = input<ComboboxFilterOptions>({});

	/** @internal The collator used for string comparison */
	public readonly collator = computed(() => {
		const options = this.filterOptions();
		const mergedOptions = { ...this._config.filterOptions, ...options };
		return new Intl.Collator(options.locale, mergedOptions);
	});

	/** A function to compare an item with the selected value. */
	public readonly isItemEqualToValue = input<ComboboxItemEqualToValue<T>>(this._config.isItemEqualToValue);

	/** A function to convert an item to a string for display. */
	public readonly itemToString = input<ComboboxItemToString<T> | undefined>(this._config.itemToString);

	/** A custom filter function to use when searching. */
	public readonly filter = input<ComboboxFilter<T>>(this._config.filter);

	/** Whether to auto-highlight the first matching item. */
	public readonly autoHighlight = input<boolean, BooleanInput>(this._config.autoHighlight, {
		transform: booleanAttribute,
	});

	/** Whether to close the popover after selecting an item. */
	public readonly closeOnSelect = input<boolean, BooleanInput>(this._config.closeOnSelect, {
		transform: booleanAttribute,
	});

	/** The selected value of the combobox. */
	public readonly value = model<T | undefined | null>(null);

	/**
	 * Determines whether a value is present (i.e., has been selected).
	 * Considers `undefined`, `null`, and empty string as "not present".
	 * Used to check if the selected value is present and to validate an item value on Enter key selection.
	 */
	public readonly isSingleValuePresent = input<(value: T | undefined | null) => boolean>(
		this._config.isSingleValuePresent,
	);
	public readonly hasValue = computed(() => this.isSingleValuePresent()(this.value()));

	/** The current search query. */
	public readonly search = model<string>('');

	private readonly _inputWidth = signal<number | null>(null);

	/** @internal The width of the search input wrapper */
	public readonly searchInputWrapperWidth = this._inputWidth.asReadonly();

	/** @internal Access all the items within the combobox */
	public readonly items = contentChildren<BrnComboboxItem<T>>(BrnComboboxItemToken, {
		descendants: true,
	});

	private readonly _content = contentChild<BrnComboboxContent>(BrnComboboxContent, {
		descendants: true,
	});

	/** Determine if the combobox has any visible items */
	public readonly visibleItems = computed(() => this.items().some((item) => item.visible()));

	/** @internal The key manager for managing active descendant */
	public readonly keyManager = new ActiveDescendantKeyManager(this.items, this._injector);

	/** @internal Whether the combobox is expanded */
	public readonly isExpanded = computed(() => this._brnPopover?.stateComputed() === 'open');

	private readonly _comboboxInput = signal<BrnComboboxInput<T> | undefined>(undefined);

	private readonly _comboboxList = signal<BrnComboboxList | undefined>(undefined);

	/** @internal The id of the combobox list, registered by BrnComboboxList. Used by the input for aria-controls. */
	public readonly listId = computed(() => this._comboboxList()?.id());

	/** @internal The id of the combobox content. Used by the trigger for aria-controls. */
	public readonly contentId = computed(() => this._content()?.id());

	public readonly mode = computed<ComboboxInputMode>(() => this._comboboxInput()?.mode() || 'combobox');

	public readonly labelableId = computed(() => this._comboboxInput()?.id());

	protected _onChange?: ChangeFn<T | undefined | null>;
	protected _onTouched?: TouchFn;

	constructor() {
		this.keyManager
			.withVerticalOrientation()
			.withHomeAndEnd()
			.withPageUpDown()
			.withWrap()
			.skipPredicate((item) => item.disabled || !item.visible());

		this._brnPopover?.closed.subscribe(() => {
			this.search.set('');
			this.keyManager.setActiveItem(-1);
			this._onTouched?.();
		});

		afterNextRender(() => {
			effect(
				() => {
					if (!this.autoHighlight() || !this.isExpanded() || !this.search()) return;

					const hasVisibleItems = this.visibleItems();

					untracked(() => {
						if (hasVisibleItems) {
							this.keyManager.setFirstItemActive();
						} else {
							this.keyManager.setActiveItem(-1);
						}
					});
				},
				{ injector: this._injector },
			);
		});
	}

	public registerComboboxInput(input: BrnComboboxInput<T>): void {
		this._comboboxInput.set(input);
	}

	/** @internal Register the combobox list. Called by BrnComboboxList in its constructor. */
	public registerComboboxList(list: BrnComboboxList): void {
		this._comboboxList.set(list);
	}

	public updateInputWidth(width: number | null): void {
		this._inputWidth.set(width);
	}

	public isSelected(itemValue: T): boolean {
		return this.isItemEqualToValue()(itemValue, this.value());
	}

	public select(itemValue: T): void {
		this.value.set(itemValue);
		this._onChange?.(itemValue);
		if (this.closeOnSelect()) {
			this.close();
		}
	}

	/** Select the active item with Enter key. */
	public selectActiveItem(): void {
		if (!this.isExpanded()) return;

		const activeItem = this.keyManager.activeItem;

		// setActiveItem() bypasses skipPredicate, so the active item may be disabled.
		if (activeItem?.disabled) return;

		const value = activeItem?.value();

		if (this.isSingleValuePresent()(value)) {
			this.select(value as T);
		} else {
			this.close();
		}
	}

	public resetValue(): void {
		this.value.set(null);
		this._onChange?.(null);
	}

	public resetSearch(): void {
		this.search.set('');
	}

	public removeValue(_: T): void {
		console.warn('BrnComboboxChipRemove only works with multiple selection comboboxes.');
	}

	public removeLastSelectedItem(): void {
		console.warn('BrnComboboxChipInput only works with multiple selection comboboxes.');
	}

	public open(): void {
		if (this._disabled() || this.isExpanded()) return;

		this._brnPopover?.open();
	}

	/** CONTROL VALUE ACCESSOR */
	public writeValue(value: T | undefined | null): void {
		this.value.set(value);
	}

	public registerOnChange(fn: ChangeFn<T | undefined | null>): void {
		this._onChange = fn;
	}

	public registerOnTouched(fn: TouchFn): void {
		this._onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean): void {
		this._disabled.set(isDisabled);
	}

	protected _onFocusOut(event: FocusEvent): void {
		const currentTarget = event.currentTarget as HTMLElement;
		const focusedEl = event.relatedTarget as HTMLElement | null;
		const contentEl = this._content()?.el.nativeElement;

		if (!currentTarget.contains(focusedEl) && !contentEl?.contains(focusedEl)) {
			if (this.isExpanded()) {
				this._brnPopover?.close();
			}
			this._onTouched?.();
		}
	}

	public close(): void {
		if (this._disabled() || !this.isExpanded()) return;

		this._brnPopover?.close();
	}
}
